import Modal from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { formatDateTime, calculateDuration } from '@/utils/timeUtils'
import { useAppStore } from '@/store'
import { useTranslation } from '@/i18n'

import { JobRunStatusTag } from './JobStatusTag'

type JobRunResultsModalProps = {
  isOpen: boolean
  onClose: () => void
  jobRunId: string | null
}

const JobRunDetailModal = ({
  isOpen,
  onClose,
  jobRunId,
}: JobRunResultsModalProps) => {
  const { jobRuns } = useAppStore()
  const { t } = useTranslation()

  const noData = (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('jobs:job_run.run_details')}
    >
      <div className="text-center text-gray-500 dark:text-dark-text-muted py-6">
        {t('jobs:job_run.no_run_available')}
      </div>
      <div className="flex justify-end mt-4">
        <Button variant="outline" onClick={onClose}>
          {t('common:buttons.close')}
        </Button>
      </div>
    </Modal>
  )

  if (jobRuns.length === 0) return noData

  const jobRun =
    jobRunId === 'latest'
      ? jobRuns.reduce((latest, current) =>
          new Date(current.started_at) > new Date(latest.started_at)
            ? current
            : latest
        )
      : jobRuns.find((run) => run.id === jobRunId)

  // console.log({ jobRunId, jobRuns, jobRun })
  if (!jobRun) return noData

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('jobs:job_run.run_details')}
    >
      <div className="grid gap-4 text-sm">
        <div className="flex justify-between">
          <span className="font-semibold">{t('jobs:job_run.status')}</span>
          <span>
            <JobRunStatusTag status={jobRun.status} />
          </span>
        </div>
        <div className="flex justify-between">
          <span className="font-semibold">{t('jobs:job_run.started_at')}</span>
          <span>{formatDateTime(jobRun.started_at)}</span>
        </div>
        <div className="flex justify-between">
          <span className="font-semibold">{t('jobs:job_run.ended_at')}</span>
          <span>
            {jobRun.ended_at
              ? formatDateTime(jobRun.ended_at)
              : t('jobs:job_run.in_progress')}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="font-semibold">{t('jobs:job_run.duration')}</span>
          <span>
            {jobRun.ended_at
              ? calculateDuration(jobRun.started_at, jobRun.ended_at)
              : '-'}
          </span>
        </div>

        {/* Results */}
        <div>
          <span className="font-semibold">{t('jobs:job_run.results')}</span>
          <div className="mt-2 p-3 border rounded-md bg-gray-50 dark:bg-dark-muted text-gray-700 dark:text-gray-300 text-xs max-h-60 overflow-auto break-words whitespace-pre-wrap text-wrap">
            <pre className="overflow-auto max-h-60 break-words whitespace-pre-wrap text-wrap">
              {jobRun.result
                ? JSON.stringify(jobRun.result, null, 2)
                : t('jobs:job_run.no_results_available')}
            </pre>
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-4">
        <Button variant="outline" onClick={onClose}>
          {t('common:buttons.close')}
        </Button>
      </div>
    </Modal>
  )
}

export default JobRunDetailModal
